import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Share2, ExternalLink, Download, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";

const C = {
  bg: "#F7F7F5",
  bgSoft: "#FFFFFF",
  ink: "#0F0F0F",
  inkSoft: "#4A4A4A",
  inkMuted: "#8A8A8A",
  accent: "#2D5544",
  accentHover: "#3A6B57",
  coral: "#B85042",
  border: "#E5E5E5",
  borderSoft: "#F0F0F0",
};

const LATO = { fontFamily: "'Lato', sans-serif" };

const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

function QuoteShareCard({ slug, quote }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${SITE_URL}/api/share/${slug}`;
  const imageUrl = `${SITE_URL}${quote.image}`;
  const fullQuote = `"${quote.title} ${quote.tail}"`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      toast.message("Native share unavailable", {
        description: "Use Copy Link instead.",
      });
      return;
    }
    try {
      await navigator.share({
        title: "The Dark Night of the Soul",
        text: fullQuote,
        url: shareUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <article
      data-testid={`share-card-${slug}`}
      style={{
        background: C.bgSoft,
        border: `1px solid ${C.border}`,
      }}
    >
      <a
        href={shareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block relative overflow-hidden"
        style={{
          aspectRatio: "1200/630",
          background: C.borderSoft,
          borderBottom: `1px solid ${C.border}`,
        }}
        data-testid={`share-card-preview-${slug}`}
      >
        <img src={imageUrl} alt={fullQuote} className="w-full h-full object-cover" />
      </a>

      <div className="p-6 md:p-8">
        <div
          style={{
            ...LATO,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: C.coral,
            marginBottom: 12,
          }}
        >
          /{slug}
        </div>
        <p
          style={{
            ...LATO,
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.35,
            color: C.ink,
          }}
        >
          &ldquo;{quote.title}{" "}
          <em style={{ fontStyle: "italic", color: C.accent }}>{quote.tail}</em>&rdquo;
        </p>

        <div
          className="mt-6 px-4 py-3 truncate"
          style={{
            background: C.bg,
            border: `1px solid ${C.borderSoft}`,
            ...LATO,
            fontSize: 11,
            color: C.inkMuted,
          }}
        >
          {shareUrl}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            onClick={copy}
            data-testid={`share-copy-${slug}`}
            className="inline-flex items-center justify-center gap-2 transition-colors duration-300"
            style={{
              background: C.accent,
              color: C.bg,
              padding: "12px 16px",
              ...LATO,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.accentHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.accent)}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
              }}
            >
              {copied ? "Copied" : "Copy Link"}
            </span>
          </button>

          <button
            onClick={nativeShare}
            data-testid={`share-native-${slug}`}
            className="inline-flex items-center justify-center gap-2 transition-colors duration-300"
            style={{
              border: `1px solid ${C.border}`,
              color: C.ink,
              padding: "12px 16px",
              background: "transparent",
              ...LATO,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = C.accent;
              e.currentTarget.style.color = C.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.ink;
            }}
          >
            <Share2 size={14} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
              }}
            >
              Share
            </span>
          </button>

          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-open-${slug}`}
            className="inline-flex items-center justify-center gap-2 transition-colors duration-300"
            style={{
              border: `1px solid ${C.borderSoft}`,
              color: C.inkSoft,
              padding: "12px 16px",
              ...LATO,
            }}
          >
            <ExternalLink size={14} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
              }}
            >
              Open
            </span>
          </a>

          <a
            href={imageUrl}
            download={`${slug}.png`}
            data-testid={`share-download-${slug}`}
            className="inline-flex items-center justify-center gap-2 transition-colors duration-300"
            style={{
              border: `1px solid ${C.borderSoft}`,
              color: C.inkSoft,
              padding: "12px 16px",
              ...LATO,
            }}
          >
            <Download size={14} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
              }}
            >
              Image
            </span>
          </a>
        </div>
      </div>
    </article>
  );
}

export default function ShareIndex() {
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/quotes")
      .then((r) => setQuotes(r.data))
      .catch((e) => {
        console.error(e);
        setError("Failed to load quotes");
      });
  }, []);

  return (
    <main
      className="min-h-screen"
      data-testid="share-index-page"
      style={{ background: C.bg, color: C.ink, ...LATO }}
    >
      <Toaster theme="light" position="top-center" />

      <header style={{ borderBottom: `1px solid ${C.border}`, background: C.bg }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="back-to-landing"
            className="inline-flex items-center gap-3 transition-colors"
            style={{
              ...LATO,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: C.inkSoft,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.ink)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.inkSoft)}
          >
            <ArrowLeft size={14} />
            Back to Landing
          </Link>
          <div style={{ ...LATO, fontSize: 18, fontWeight: 700, color: C.ink }}>
            Robin{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>Weyersberg</span>
          </div>
        </div>
      </header>

      <section className="px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12 md:pb-20 max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <span style={{ height: 1, width: 40, background: C.coral }} />
          <span
            style={{
              ...LATO,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: C.coral,
            }}
          >
            Share Kit
          </span>
        </div>
        <h1
          className="max-w-4xl"
          style={{
            ...LATO,
            fontWeight: 700,
            fontSize: "clamp(40px, 6vw, 88px)",
            lineHeight: 0.98,
            letterSpacing: "-0.02em",
            color: C.ink,
          }}
        >
          Three quotes.{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>
            Three doorways
          </span>{" "}
          back to the guide.
        </h1>
        <p
          className="max-w-2xl"
          style={{
            ...LATO,
            fontSize: 18,
            lineHeight: 1.6,
            color: C.ink,
            marginTop: 32,
          }}
        >
          One-click share assets for Instagram, X, LinkedIn — and anywhere else
          your audience is reading. Each link auto-renders a cinematic preview
          card and drops visitors on a standalone quote page with a single CTA
          back to the 47€ guide (UTM-tagged for tracking).
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-32 max-w-7xl mx-auto">
        {error && (
          <div
            data-testid="share-index-error"
            style={{
              border: `1px solid ${C.border}`,
              padding: 32,
              color: C.inkSoft,
            }}
          >
            {error}
          </div>
        )}

        {!quotes && !error && (
          <div
            data-testid="share-index-loading"
            style={{
              ...LATO,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: C.inkMuted,
            }}
          >
            Loading quotes…
          </div>
        )}

        {quotes && (
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="share-index-grid"
          >
            {Object.entries(quotes).map(([slug, q]) => (
              <QuoteShareCard key={slug} slug={slug} quote={q} />
            ))}
          </div>
        )}
      </section>

      <footer
        className="px-6 md:px-12 lg:px-16 py-10 max-w-7xl mx-auto"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            ...LATO,
            fontSize: 10,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: C.inkMuted,
          }}
        >
          <span>© {new Date().getFullYear()} Robin Weyersberg</span>
          <span>Share kit · Internal use</span>
        </div>
      </footer>
    </main>
  );
}
